import React, { useMemo, useState } from 'react';
import {
  MicOnIcon,
  MicOffIcon,
  CamOnIcon,
  CamOffIcon,
  MoreIcon,
} from '../MeetingIcons';
import AudioOptionsMenu from '../AudioOptionsMenu';
import DeviceMenuList from '../DeviceMenuList';
import useMicLevel from '../../hooks/useMicLevel';
import {
  splitShellStyle,
  splitWrapStyle,
  deviceMenuStyle,
  deviceMenuItemStyle,
  getMoreButtonStyle,
} from '../../styles/deviceMenuStyles';
import {
  micBarsWrapStyle,
  micMainContentStyle,
  getMainButtonStyle,
  buildMicBars,
} from '../../styles/micTesterStyles';

const WaitingRoomControls = ({
  t,
  muted,
  onToggleMute,
  cameraOn,
  onCameraToggle,
  showMicMenu,
  setShowMicMenu,
  showCameraMenu,
  setShowCameraMenu,
  micDevices,
  activeMicDeviceId,
  onSelectMic,
  includeMicTester = false,
  speakerDevices,
  activeSpeakerDeviceId,
  onSelectSpeaker,
  onSpeakerTest,
  cameraDevices,
  activeCameraDeviceId,
  onSelectCamera,
}) => {
  const [micHovered, setMicHovered] = useState(false);
  const [camHovered, setCamHovered] = useState(false);
  const [micMoreHovered, setMicMoreHovered] = useState(false);
  const [camMoreHovered, setCamMoreHovered] = useState(false);

  const micButtonStyle = useMemo(
    () => getMainButtonStyle({ off: muted, hovered: micHovered, includeMicTester }),
    [muted, micHovered, includeMicTester],
  );
  const cameraButtonStyle = useMemo(() => getMainButtonStyle({ off: !cameraOn, hovered: camHovered }), [cameraOn, camHovered]);
  const micMoreButtonStyle = useMemo(() => getMoreButtonStyle(micMoreHovered), [micMoreHovered]);
  const camMoreButtonStyle = useMemo(() => getMoreButtonStyle(camMoreHovered), [camMoreHovered]);
  const micLevel = useMicLevel({
    muted: muted || !includeMicTester,
    deviceId: activeMicDeviceId,
  });
  const micBars = useMemo(() => {
    if (!includeMicTester) {
      return [];
    }

    return buildMicBars({ muted, micLevel });
  }, [micLevel, muted, includeMicTester]);

  return (
    <>
      <div style={splitWrapStyle} data-wr-mic-wrap="true">
        <div style={splitShellStyle}>
          <button
            type="button"
            className="waiting-room-tooltip"
            style={micButtonStyle}
            onClick={onToggleMute}
            onMouseEnter={() => setMicHovered(true)}
            onMouseLeave={() => setMicHovered(false)}
            data-tooltip={muted ? t('VideoChatV2.audio.unmuteMicrophone') : t('VideoChatV2.audio.muteMicrophone')}
            aria-label={muted ? t('VideoChatV2.audio.unmuteMicrophone') : t('VideoChatV2.audio.muteMicrophone')}
          >
            <span style={micMainContentStyle}>
              {includeMicTester ? (
                <span style={micBarsWrapStyle} aria-hidden="true">
                  {micBars.map((style, idx) => (
                    <span key={idx} style={style} />
                  ))}
                </span>
              ) : null}
              {muted ? <MicOffIcon /> : <MicOnIcon />}
            </span>
          </button>
          <button
            type="button"
            className="waiting-room-tooltip"
            style={micMoreButtonStyle}
            onClick={() => {
              setShowCameraMenu(false);
              setShowMicMenu((prev) => !prev);
            }}
            onMouseEnter={() => setMicMoreHovered(true)}
            onMouseLeave={() => setMicMoreHovered(false)}
            data-tooltip={t('VideoChatV2.audio.options')}
            aria-label={t('VideoChatV2.audio.options')}
          >
            <MoreIcon />
          </button>
        </div>
        {showMicMenu ? (
          <AudioOptionsMenu
            t={t}
            micDevices={micDevices}
            activeMicDeviceId={activeMicDeviceId}
            onSelectMic={onSelectMic}
            speakerDevices={speakerDevices}
            activeSpeakerDeviceId={activeSpeakerDeviceId}
            onSelectSpeaker={onSelectSpeaker}
            onTestSpeaker={onSpeakerTest}
            menuStyle={deviceMenuStyle}
            getItemStyle={deviceMenuItemStyle}
          />
        ) : null}
      </div>

      <div style={splitWrapStyle} data-wr-cam-wrap="true">
        <div style={splitShellStyle}>
          <button
            type="button"
            className="waiting-room-tooltip"
            style={cameraButtonStyle}
            onClick={onCameraToggle}
            onMouseEnter={() => setCamHovered(true)}
            onMouseLeave={() => setCamHovered(false)}
            data-tooltip={cameraOn ? t('VideoChatV2.camera.turnOff') : t('VideoChatV2.camera.turnOn')}
            aria-label={cameraOn ? t('VideoChatV2.camera.turnOff') : t('VideoChatV2.camera.turnOn')}
          >
            {cameraOn ? <CamOnIcon /> : <CamOffIcon />}
          </button>
          <button
            type="button"
            className="waiting-room-tooltip"
            style={camMoreButtonStyle}
            onClick={() => {
              setShowMicMenu(false);
              setShowCameraMenu((prev) => !prev);
            }}
            onMouseEnter={() => setCamMoreHovered(true)}
            onMouseLeave={() => setCamMoreHovered(false)}
            data-tooltip={t('VideoChatV2.camera.choose')}
            aria-label={t('VideoChatV2.camera.choose')}
          >
            <MoreIcon />
          </button>
        </div>
        {showCameraMenu ? (
          <DeviceMenuList
            devices={cameraDevices}
            activeDeviceId={activeCameraDeviceId}
            onSelect={onSelectCamera}
            menuAriaLabel={t('VideoChatV2.camera.label')}
            menuStyle={deviceMenuStyle}
            getItemStyle={(active) => deviceMenuItemStyle(active)}
          />
        ) : null}
      </div>
    </>
  );
};

export default React.memo(WaitingRoomControls);
